import { useRef, useState } from "react";
import { Camera, Image as ImageIcon, FolderOpen, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// Tři skryté vstupy: MIUI ukáže foťák jen s `capture`, iOS ukáže knihovnu jen
// BEZ něj a Android otevře správce souborů jen při výčtu přípon (s image/*
// otevře galerii). Nikdy je neslučovat do jednoho vstupu.
//
// open() = jedno tlačítko „Nahrát fotky“:
//   iPhone/iPad → rovnou nativní nabídka iOS (Vyfotit / Knihovna / Soubory),
//   Android     → naše nabídka Vyfotit / Vybrat z galerie / Vybrat soubor,
//   počítač     → okno pro výběr souboru.
const FILE_EXT = ".jpg,.jpeg,.png,.webp,.heic,.heif";

const isIOS = () => typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
const isAndroid = () => typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

// 332: pdf = přijmout i PDF (dodací list z e-mailu): galerie / výběr souboru nabídne i PDF, foťák dál jen fotky
export function usePhotoInputs({ accept = "image/*", multiple = true, onChange, disabled, testId, pdf = false } = {}) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const fileRef = useRef(null);
  const [menu, setMenu] = useState(false);
  const pick = (r) => { setMenu(false); r.current?.click(); };

  const inputs = (
    <>
      <input ref={camRef} type="file" accept={accept} capture="environment" multiple={multiple}
        onChange={onChange} className="hidden" disabled={disabled} data-testid="photo-input-camera" />
      <input ref={galRef} type="file" accept={pdf ? `${accept},application/pdf,.pdf` : accept} multiple={multiple}
        onChange={onChange} className="hidden" disabled={disabled} data-testid={testId || "photo-input-gallery"} />
      <input ref={fileRef} type="file" accept={pdf ? `${FILE_EXT},.pdf,application/pdf` : FILE_EXT} multiple={multiple}
        onChange={onChange} className="hidden" disabled={disabled} data-testid="photo-input-file" />
      <Dialog open={menu} onOpenChange={setMenu}>
        <DialogContent sheet hideClose className="max-w-sm p-3 gap-1 max-md:pt-8" data-testid="photo-source-sheet">
          <DialogTitle className="text-center text-[13px] font-semibold text-muted-foreground pb-2">{pdf ? "Přidat foto nebo PDF" : "Nahrát fotky"}</DialogTitle>
          {[
            [Camera, "Vyfotit", camRef, "photo-source-camera"],
            [ImageIcon, "Vybrat z galerie", galRef, "photo-source-gallery"],
            [FolderOpen, pdf ? "Vybrat soubor (PDF)" : "Vybrat soubor", fileRef, "photo-source-file"],
          ].map(([Icon, label, r, tid]) => (
            <button key={tid} type="button" onClick={() => pick(r)} data-testid={tid}
              className="w-full flex items-center gap-3 h-14 px-3 rounded-[14px] text-[15px] font-semibold hover:bg-secondary active:bg-secondary transition-colors">
              <span className="w-9 h-9 rounded-full grid place-items-center bg-brand/10 text-brand shrink-0"><Icon size={18} /></span>
              {label}
            </button>
          ))}
          <button type="button" onClick={() => setMenu(false)} data-testid="photo-source-cancel"
            className="mt-1 w-full h-12 rounded-[14px] bg-secondary font-bold">Zrušit</button>
        </DialogContent>
      </Dialog>
    </>
  );

  const open = () => {
    if (disabled) return;
    if (isAndroid()) setMenu(true);
    else galRef.current?.click();   // iOS: nativní nabídka, desktop: výběr souboru
  };

  return {
    inputs,
    open,
    isIOS: isIOS(),
    openCamera: () => camRef.current?.click(),
    openGallery: () => galRef.current?.click(),
  };
}

// Jedno velké tlačítko „Nahrát fotky“ (odsouhlasený design C) — všude stejné:
// odhlášení, uzavření dne, úkoly, deník, galerie. Na desktopu přijímá i přetažení.
export function PhotoUploadButton({ onClick, busy = false, disabled = false, dragging = false, label = "Nahrát fotky", hint, progress, testid = "photo-upload-btn", className = "" }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled || busy} data-testid={testid}
      className={`w-full min-h-[56px] px-4 py-3 rounded-[16px] border-[1.5px] border-dashed flex items-center justify-center gap-2.5 font-extrabold text-[15px] transition-colors disabled:opacity-50
        ${dragging ? "border-brand bg-brand/15 text-brand" : "border-brand/70 bg-brand/[0.05] text-brand hover:bg-brand/10"} ${className}`}>
      {busy ? <Loader2 size={20} className="animate-spin motion-reduce:animate-none" /> : <Camera size={20} />}
      <span className="flex flex-col items-start leading-tight">
        <span className="tabular-nums">{dragging ? "Pusťte fotky sem" : busy ? (progress?.total > 1 ? `Nahrávám ${progress.done} z ${progress.total}…` : "Nahrávám…") : label}</span>
        {hint && !busy && !dragging && <span className="text-[11.5px] font-medium text-muted-foreground">{hint}</span>}
      </span>
    </button>
  );
}
